import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useAnalysisStore } from './analysisStore.js';

/**
 * Graph store — per-tab Graph settings for the Analysis view.
 *
 * A "Graph" is a ChamberGraph panel. Each chamber with data gets one auto-
 * seeded on file load; users can add chamberless extras for device signals.
 *
 * Mutations come through GraphOptionsDialog as a single atomic
 * `applyGraphDraft(tabId, graphs)` (Apply/OK), with `setTimeAxisForAllGraphs`
 * reserved for live, all-graphs-synchronous time-axis edits. The radio-
 * selection on each plot uses `setSelectedTrace`.
 *
 * State shape:
 *   tabGraphs: { [tabId]: Graph[] }
 *   Graph: {
 *     id, kind: 'chamber' | 'extra', chamberIndex | null, label,
 *     scaling: { xMin, xMax, xUnit, y1Min, y1Max, y2Min, y2Max },
 *     traces: [{ id, channel, chamberIndex, color, yAxis }],
 *     advanced: { showMarks, showEvents, showInstrumentAndNotificationEvents },
 *     selectedTraceId,
 *   }
 */

let traceCounter = 0;
function generateTraceId() {
  return `trace-${Date.now()}-${++traceCounter}`;
}

export const TRACE_COLOR_PALETTE = [
  { value: 'blue', label: 'blue', hex: '#2C0DF1' },
  { value: 'red', label: 'red', hex: '#F5426A' },
  { value: 'green', label: 'green', hex: '#2E8B57' },
  { value: 'purple', label: 'purple', hex: '#8A2BE2' },
  { value: 'orange', label: 'orange', hex: '#FF8C00' },
  { value: 'teal', label: 'teal', hex: '#008B8B' },
  { value: 'pink', label: 'pink', hex: '#DB7093' },
  { value: 'gray', label: 'gray', hex: '#6E7B85' },
];

const TRACE_COLOR_HEX_BY_VALUE = new Map(TRACE_COLOR_PALETTE.map((c) => [c.value, c.hex]));
const FALLBACK_TRACE_COLOR = '#394b57';

export function resolveTraceColor(colorValue) {
  return TRACE_COLOR_HEX_BY_VALUE.get(colorValue) ?? FALLBACK_TRACE_COLOR;
}

export const DATA_TYPE_META = {
  O2: { label: 'O₂ concentration', unit: 'µM', defaultColor: 'blue', defaultAxis: 'y1' },
  O2Flux: { label: 'O₂ slope neg.', unit: 'pmol·s⁻¹·mL⁻¹', defaultColor: 'red', defaultAxis: 'y2' },
  ElectronicsTemperature: {
    label: 'Electronics temperature',
    unit: '°C',
    defaultColor: 'green',
    defaultAxis: 'y2',
  },
  ChamberTemperature: {
    label: 'Chamber temperature',
    unit: '°C',
    defaultColor: 'teal',
    defaultAxis: 'y2',
  },
  AirPressure: { label: 'Air pressure', unit: 'kPa', defaultColor: 'purple', defaultAxis: 'y2' },
  PeltierPower: { label: 'Peltier power', unit: 'W', defaultColor: 'orange', defaultAxis: 'y2' },
  RoomTemperature: {
    label: 'Room temperature',
    unit: '°C',
    defaultColor: 'pink',
    defaultAxis: 'y2',
  },
  ExternalTemperature: {
    label: 'External temperature',
    unit: '°C',
    defaultColor: 'gray',
    defaultAxis: 'y2',
  },
  ElectronicsHumidity: {
    label: 'Electronics humidity',
    unit: '%',
    defaultColor: 'teal',
    defaultAxis: 'y2',
  },
};

function buildInitialScaling(plotConfigList, data) {
  const o2Config = plotConfigList?.find((c) => c.data_type === 'O2');
  const fluxConfig = plotConfigList?.find((c) => c.data_type === 'O2Flux');
  const firstTime = data?.timeSeries?.[0] ?? 0;
  const lastTime = data?.timeSeries?.[data.timeSeries.length - 1] ?? 0;
  return {
    xMin: o2Config?.xmin ?? firstTime,
    xMax: o2Config?.xmax ?? lastTime,
    xUnit: o2Config?.xunit ?? 'hh:mm:ss',
    y1Min: o2Config?.ymin ?? null,
    y1Max: o2Config?.ymax ?? null,
    y2Min: fluxConfig?.ymin ?? null,
    y2Max: fluxConfig?.ymax ?? null,
  };
}

function shallowEqualObj(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (a[k] !== b[k]) return false;
  return true;
}

function tracesEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!shallowEqualObj(a[i], b[i])) return false;
  }
  return true;
}

function graphsShallowEqual(a, b) {
  if (a === b) return true;
  return (
    a.id === b.id &&
    a.kind === b.kind &&
    a.chamberIndex === b.chamberIndex &&
    a.label === b.label &&
    a.selectedTraceId === b.selectedTraceId &&
    shallowEqualObj(a.scaling, b.scaling) &&
    shallowEqualObj(a.advanced, b.advanced) &&
    tracesEqual(a.traces, b.traces)
  );
}

function buildDefaultChamberGraph(chamberIndex, chamberState) {
  const traces = [];
  if (chamberState?.data?.oxygenSeries) {
    traces.push({
      id: generateTraceId(),
      channel: 'O2',
      chamberIndex,
      color: 'blue',
      yAxis: 'y1',
    });
  }
  if (chamberState?.data?.fluxSeries) {
    traces.push({
      id: generateTraceId(),
      channel: 'O2Flux',
      chamberIndex,
      color: 'red',
      yAxis: 'y2',
    });
  }
  return {
    id: `chamber-${chamberIndex}`,
    kind: 'chamber',
    chamberIndex,
    label: `Chamber ${chamberIndex}`,
    scaling: buildInitialScaling(chamberState?.plotConfig, chamberState?.data),
    traces,
    advanced: {
      showMarks: true,
      showEvents: true,
      showInstrumentAndNotificationEvents: true,
    },
    selectedTraceId: traces[0]?.id ?? null,
  };
}

export const useGraphStore = create(
  subscribeWithSelector((set, get) => ({
    tabGraphs: {},

    // ── Lifecycle (called by analysisStore) ───────────────────────────

    seedGraphsForTab: (tabId) => {
      const tab = useAnalysisStore.getState().openFiles[tabId];
      if (!tab) return;
      const graphs = Object.entries(tab.chambers || {})
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([chamberIdx, ch]) => buildDefaultChamberGraph(Number(chamberIdx), ch));
      set((state) => ({ tabGraphs: { ...state.tabGraphs, [tabId]: graphs } }));
    },

    clearGraphsForTab: (tabId) => {
      set((state) => {
        const { [tabId]: _removed, ...rest } = state.tabGraphs;
        return { tabGraphs: rest };
      });
    },

    // ── Mutations ─────────────────────────────────────────────────────

    _updateGraphs: (tabId, updater) => {
      set((state) => {
        const current = state.tabGraphs[tabId] || [];
        const next = typeof updater === 'function' ? updater(current) : updater;
        return { tabGraphs: { ...state.tabGraphs, [tabId]: next } };
      });
    },

    // Synchronous time axis: same time-axis patch applied to every graph.
    setTimeAxisForAllGraphs: (tabId, patch) => {
      get()._updateGraphs(tabId, (graphs) =>
        graphs.map((g) => ({ ...g, scaling: { ...g.scaling, ...patch } })),
      );
    },

    setSelectedTrace: (tabId, graphId, traceId) => {
      get()._updateGraphs(tabId, (graphs) =>
        graphs.map((g) => (g.id === graphId ? { ...g, selectedTraceId: traceId } : g)),
      );
    },

    // Atomic commit from the dialog. Reuses existing graph references for
    // entries whose values are unchanged so editing one graph doesn't churn
    // every other plot's downstream memos (baseXScale/baseYScale/etc.) and
    // rAF subscriptions — that cascade was a trigger for the passive-plot
    // "Maximum update depth" loop on Apply.
    applyGraphDraft: (tabId, graphs) => {
      get()._updateGraphs(tabId, (current) => {
        const byId = new Map(current.map((g) => [g.id, g]));
        return graphs.map((next) => {
          const prev = byId.get(next.id);
          return prev && graphsShallowEqual(prev, next) ? prev : next;
        });
      });
    },
  })),
);
